import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Avatar,
  IconButton,
  Card,
  CardContent,
  InputAdornment,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { PhotoCamera, Save, Logout, ContentCopy, QrCode, Visibility, Download } from '@mui/icons-material';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useAuth } from '../context/AuthContext';
import { memberAPI } from '../services/api';
import koalogo from '../assets/koalogo.png';
import MemberPortalFooter from './MemberPortalFooter';

const Profile = () => {
  const { member, logout, updateMember } = useAuth();
  const [formData, setFormData] = useState({
    name: member?.name || '',
    email: member?.email || '',
    photo: member?.photo || '',
    IOA_LM_number: member?.IOA_LM_number || '',
    working_hospital_name: member?.working_hospital_name || '',
    working_hospital_district: member?.working_hospital_district || '',
    designation: member?.designation || '',
    communication_address: member?.communication_address || '',
    address1_place_post: member?.address1_place_post || '',
    district: member?.district || '',
    pincode: member?.pincode || '',
    state: member?.state || '',
    district_club_name: member?.district_club_name || '',
    mobile_number: member?.mobile_number || '',
    whatsapp_number: member?.whatsapp_number || '',
    landline_hospital: member?.landline_hospital || '',
    landline_residence: member?.landline_residence || '',
    date_of_birth: member?.date_of_birth || '',
    blood_group: member?.blood_group || '',
    tcmc_reg_no: member?.tcmc_reg_no || '',
    ima_life_membership_status: member?.ima_life_membership_status || '',
    mbbs_college: member?.mbbs_college || '',
    mbbs_year: member?.mbbs_year || '',
    pg_diploma_college: member?.pg_diploma_college || '',
    pg_diploma_year: member?.pg_diploma_year || '',
    pg_degree_college: member?.pg_degree_college || '',
    pg_degree_year: member?.pg_degree_year || '',
    awards_honours: member?.awards_honours || '',
    spouse_name: member?.spouse_name || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCrop, setShowCrop] = useState(false);
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState({ unit: '%', width: 90, height: 90, aspect: 1 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);

  // Update formData when member data changes
  React.useEffect(() => {
    if (member) {
      setFormData({
        name: member.name || '',
        email: member.email || '',
        photo: member.photo || '',
        IOA_LM_number: member.IOA_LM_number || '',
        working_hospital_name: member.working_hospital_name || '',
        working_hospital_district: member.working_hospital_district || '',
        designation: member.designation || '',
        communication_address: member.communication_address || '',
        address1_place_post: member.address1_place_post || '',
        district: member.district || '',
        pincode: member.pincode || '',
        state: member.state || '',
        district_club_name: member.district_club_name || '',
        mobile_number: member.mobile_number || '',
        whatsapp_number: member.whatsapp_number || '',
        landline_hospital: member.landline_hospital || '',
        landline_residence: member.landline_residence || '',
        date_of_birth: member.date_of_birth || '',
        blood_group: member.blood_group || '',
        tcmc_reg_no: member.tcmc_reg_no || '',
        ima_life_membership_status: member.ima_life_membership_status || '',
        mbbs_college: member.mbbs_college || '',
        mbbs_year: member.mbbs_year || '',
        pg_diploma_college: member.pg_diploma_college || '',
        pg_diploma_year: member.pg_diploma_year || '',
        pg_degree_college: member.pg_degree_college || '',
        pg_degree_year: member.pg_degree_year || '',
        awards_honours: member.awards_honours || '',
        spouse_name: member.spouse_name || '',
      });
    }
  }, [member]);

  // Generate public profile URL
  const publicProfileUrl = member?.KOALM_number
    ? `${window.location.origin}/public/${member.KOALM_number}`
    : null;

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        const result = reader.result;
        if (result) {
          setImgSrc(result.toString());
          setShowCrop(true);
          setCompletedCrop(null); // Reset completed crop
        }
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoaded = useCallback((img) => {
    imgRef.current = img;
    // Reset crop when new image loads
    setCrop({ unit: '%', width: 90, height: 90, aspect: 1 });
  }, []);

  const getCroppedImg = (image, crop) => {
    const canvas = document.createElement('canvas');
    
    // Get the displayed image dimensions
    const displayedWidth = image.width;
    const displayedHeight = image.height;
    
    // Calculate scale factors
    const scaleX = image.naturalWidth / displayedWidth;
    const scaleY = image.naturalHeight / displayedHeight;
    
    // Calculate pixel values from percentage or pixel crop
    let pixelCrop;
    if (crop.unit === '%') {
      pixelCrop = {
        x: (crop.x / 100) * image.naturalWidth,
        y: (crop.y / 100) * image.naturalHeight,
        width: (crop.width / 100) * image.naturalWidth,
        height: (crop.height / 100) * image.naturalHeight,
      };
    } else {
      pixelCrop = {
        x: crop.x * scaleX,
        y: crop.y * scaleY,
        width: crop.width * scaleX,
        height: crop.height * scaleY,
      };
    }
    
    // Ensure square output (use the smaller dimension)
    const size = Math.min(pixelCrop.width, pixelCrop.height);
    
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    // Draw the cropped image centered in the square canvas
    const sourceX = pixelCrop.x + (pixelCrop.width - size) / 2;
    const sourceY = pixelCrop.y + (pixelCrop.height - size) / 2;

    ctx.drawImage(
      image,
      sourceX,
      sourceY,
      size,
      size,
      0,
      0,
      size,
      size
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        const reader = new FileReader();
        reader.addEventListener('load', () => {
          resolve(reader.result);
        });
        reader.readAsDataURL(blob);
      }, 'image/jpeg', 0.9);
    });
  };

  const handleCropComplete = async () => {
    if (!imgRef.current || !completedCrop) {
      return;
    }

    try {
      const croppedImageBase64 = await getCroppedImg(imgRef.current, completedCrop);
      setFormData({ ...formData, photo: croppedImageBase64 });
      setShowCrop(false);
      setImgSrc('');
    } catch (err) {
      setError('Failed to crop image');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await memberAPI.updateProfile(formData);
      if (response.success) {
        updateMember(response.member);
        setSuccess('Profile updated successfully!');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const handleCopyUrl = () => {
    if (publicProfileUrl) {
      navigator.clipboard.writeText(publicProfileUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleDownloadBadge = async () => {
    try {
      setLoading(true);
      const response = await memberAPI.downloadBadge();
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `KOA_Badge_${member?.KOALM_number || 'badge'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess('Badge downloaded successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to download badge');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 3,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={10}
          sx={{
            padding: 4,
            borderRadius: 2,
            background: 'white',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="img"
                src={koalogo}
                alt="Kerala Orthopaedic Association"
                sx={{
                  width: '80px',
                  height: 'auto',
                }}
              />
              <Box>
                <Typography variant="h4" component="h1" fontWeight="bold">
                  KERALA ORTHOPAEDIC ASSOCIATION
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Member Portal
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Logout />}
              onClick={handleLogout}
              color="error"
            >
              Logout
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Public Profile URL Card */}
          {publicProfileUrl && !showCrop && (
            <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <QrCode color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Your Public Profile URL
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Share this URL or use it to generate a QR code for your badge:
                </Typography>
                <TextField
                  fullWidth
                  value={publicProfileUrl}
                  readOnly
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleCopyUrl} color="primary">
                          <ContentCopy />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                />
                {copySuccess && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    URL copied to clipboard!
                  </Alert>
                )}
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Visibility />}
                    onClick={() => window.open(publicProfileUrl, '_blank')}
                    sx={{ py: 1 }}
                  >
                    View Public Profile
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Download />}
                    onClick={handleDownloadBadge}
                    disabled={loading}
                    sx={{ py: 1 }}
                  >
                    Download Badge
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Download Badge Button - Always visible when not cropping */}
          {!showCrop && (
            <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Download color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Download Your Badge
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Download your official KOA member badge with your photo, name, KOALM number, and QR code.
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<Download />}
                  onClick={handleDownloadBadge}
                  disabled={loading || !member?.KOALM_number}
                  size="large"
                  sx={{ py: 1.5 }}
                >
                  {loading ? 'Generating Badge...' : 'Download Badge PDF'}
                </Button>
                {!member?.KOALM_number && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Please ensure your KOALM number is set in your profile to download the badge.
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {showCrop ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Crop Your Photo
              </Typography>
              <Box 
                sx={{ 
                  mb: 2, 
                  display: 'flex', 
                  justifyContent: 'center',
                  maxWidth: '100%',
                  overflow: 'hidden'
                }}
              >
                {imgSrc && (
                  <ReactCrop
                    src={imgSrc}
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                    onImageLoaded={onImageLoaded}
                    aspect={1}
                    style={{ maxWidth: '100%', maxHeight: '500px' }}
                  >
                    <img
                      src={imgSrc}
                      alt="Crop preview"
                      style={{ maxWidth: '100%', display: 'block' }}
                      onLoad={(e) => onImageLoaded(e.target)}
                    />
                  </ReactCrop>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleCropComplete}
                  disabled={!completedCrop}
                >
                  Apply Crop
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowCrop(false);
                    setImgSrc('');
                    setCompletedCrop(null);
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sx={{ textAlign: 'center' }}>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Avatar
                      src={formData.photo}
                      sx={{
                        width: 150,
                        height: 150,
                        border: '3px solid',
                        borderColor: 'primary.main',
                        objectFit: 'cover',
                        '& img': {
                          objectFit: 'cover',
                          width: '100%',
                          height: '100%',
                        },
                      }}
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={onSelectFile}
                      style={{ display: 'none' }}
                    />
                    <IconButton
                      color="primary"
                      aria-label="upload photo"
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        bgcolor: 'white',
                        '&:hover': { bgcolor: 'grey.100' },
                      }}
                    >
                      <PhotoCamera />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Click camera icon to upload and crop photo
                  </Typography>
                </Grid>

                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                    Basic Information
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Name *"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email *"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="IOA LM No"
                    name="IOA_LM_number"
                    value={formData.IOA_LM_number}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Date of Birth *"
                    name="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={handleInputChange}
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Blood Group *"
                    name="blood_group"
                    value={formData.blood_group}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Name of Spouse"
                    name="spouse_name"
                    value={formData.spouse_name}
                    onChange={handleInputChange}
                  />
                </Grid>

                {/* Professional Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                    Professional Information
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Name of Working Hospital*"
                    name="working_hospital_name"
                    value={formData.working_hospital_name}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="District of Working Hospital"
                    name="working_hospital_district"
                    value={formData.working_hospital_district}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Designation *"
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="TCMC Reg No *"
                    name="tcmc_reg_no"
                    value={formData.tcmc_reg_no}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">IMA Life Membership Status</FormLabel>
                    <RadioGroup
                      row
                      name="ima_life_membership_status"
                      value={formData.ima_life_membership_status || ''}
                      onChange={handleInputChange}
                    >
                      <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="No" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {/* Education */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                    Education
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="MBBS (College & Year)"
                    name="mbbs_college"
                    value={formData.mbbs_college}
                    onChange={handleInputChange}
                    placeholder="College name"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="MBBS Year"
                    name="mbbs_year"
                    value={formData.mbbs_year}
                    onChange={handleInputChange}
                    placeholder="Year"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="PG Diploma (College & Year)"
                    name="pg_diploma_college"
                    value={formData.pg_diploma_college}
                    onChange={handleInputChange}
                    placeholder="College name"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="PG Diploma Year"
                    name="pg_diploma_year"
                    value={formData.pg_diploma_year}
                    onChange={handleInputChange}
                    placeholder="Year"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="PG Degree (College & Year)"
                    name="pg_degree_college"
                    value={formData.pg_degree_college}
                    onChange={handleInputChange}
                    placeholder="College name"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="PG Degree Year"
                    name="pg_degree_year"
                    value={formData.pg_degree_year}
                    onChange={handleInputChange}
                    placeholder="Year"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Awards / Honours"
                    name="awards_honours"
                    value={formData.awards_honours}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                  />
                </Grid>

                {/* Contact Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                    Contact Information
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Mobile No *"
                    name="mobile_number"
                    value={formData.mobile_number}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="WhatsApp No (Omit if same as Mobile)"
                    name="whatsapp_number"
                    value={formData.whatsapp_number}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Land Line No (Hospital)"
                    name="landline_hospital"
                    value={formData.landline_hospital}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Land Line (Residence)"
                    name="landline_residence"
                    value={formData.landline_residence}
                    onChange={handleInputChange}
                  />
                </Grid>

                {/* Address Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                    Address Information
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Communication Address (House Name / No) *"
                    name="communication_address"
                    value={formData.communication_address}
                    onChange={handleInputChange}
                    required
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address1 (Place & Post office) *"
                    name="address1_place_post"
                    value={formData.address1_place_post}
                    onChange={handleInputChange}
                    required
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="District *"
                    name="district"
                    value={formData.district}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Pincode *"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="State"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="District Club Name *"
                    name="district_club_name"
                    value={formData.district_club_name}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                    disabled={loading}
                    sx={{ py: 1.5 }}
                  >
                    {loading ? 'Saving...' : 'Save Profile'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Paper>
      </Container>
      <MemberPortalFooter />
    </Box>
  );
};

export default Profile;

